import { compressChecklistPhotoWeb } from '@/utils/media/compressChecklistPhotoWeb';
import { isStorageMediaReference } from '@/utils/media/partnerStorageReference';
import { useChecklistMediaPreview } from '@/hooks/useChecklistMediaPreview';
import { useRetainedLocalMediaPreview } from '@/hooks/useRetainedLocalMediaPreview';
import { ChecklistImageLoadNotice } from './ChecklistImageLoadNotice';
import { subscribeResumedChecklistMedia } from '@/services/checklist/ChecklistImageService';
import { showChecklistMediaFeedback } from '@/services/checklist/checklistMediaFeedback';
import { mediaErrorCode } from '@/services/diagnostics/MediaDiagnosticHistory';
import { beginMediaAttempt, finishMediaAttempt } from '@/services/diagnostics/mediaAttemptRuntime';
import { captureSentryExceptionWithScope } from '@/services/monitoring/sentry';
import { createMediaDiagnosticRecorder } from '@/services/diagnostics/mediaDiagnosticRuntime';
import React, { useState, useEffect } from 'react';
import { View, Image, StyleSheet, TouchableOpacity, ActivityIndicator, Text, Platform, Alert, Modal } from 'react-native';
import * as ImagePicker from 'expo-image-picker';
import * as FileSystem from 'expo-file-system/legacy';
import * as ImageManipulator from 'expo-image-manipulator';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import theme from '../../config/theme';
import { useActionSheet } from '@expo/react-native-action-sheet';
import { useWebAlert, AlertButton } from '../WebAlert';
import { uploadChecklistPhoto, deleteChecklistPhoto } from '../../services/checklist/ChecklistImageService';
import { databaseService } from '../../services/databaseService';
import { APP_CONSTANTS } from '../../config/constants';
import { v4 as uuidv4 } from 'uuid';
import { ChecklistPhotoFieldLogger } from '@/utils/logger';
import { traceImage } from '@/utils/debugImageTrace';
import { pickImageFromLibrary, takePhotoFromCamera } from '@/utils/media/imagePicker';
import {
    clearRecoveredCameraAsset,
    getRecoveredCameraAsset,
} from '@/utils/media/pendingImagePickerRecovery';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { getAndroidActionSheetSafeAreaStyle } from '@/utils/safeArea';
import { downloadChecklistPhoto } from '@/utils/media/downloadChecklistPhoto';
import { addSentryBreadcrumb, captureSentryException } from '@/services/monitoring/sentry';

// Tipagem das props do campo de foto do checklist
interface ChecklistPhotoFieldProps {
    empresaId: string;
    checklistId: string;
    fieldId: string;
    value?: string;
    label?: string;
    hideLabel?: boolean;
    compactCard?: boolean;
    editable?: boolean;
    onChange: (uri: string) => void;
    onPhotoFlowStateChange?: (state: {
        fieldId: string;
        active: boolean;
        reason: string;
        at: number;
    }) => void;
    maxPhotos: number;
}

/**
 * Campo de foto para checklist
 * Permite tirar foto ou escolher da galeria, faz upload local-first e atualiza o draft
 */
const ChecklistPhotoField: React.FC<ChecklistPhotoFieldProps> = ({
    empresaId,
    checklistId,
    fieldId,
    value,
    onChange,
    onPhotoFlowStateChange,
    maxPhotos,
    label = 'Foto',
    hideLabel = false,
    compactCard = false,
    editable = true,
}) => {
    const [uploading, setUploading] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [previewUri, setPreviewUri] = useState<string | null>(null);
    const previewCleanup = React.useRef({ mounted: false, uris: new Set<string>() });
    const [viewingPhoto, setViewingPhoto] = useState(false);
    const { showActionSheetWithOptions } = useActionSheet();
    const { showAlert } = useWebAlert();
    useEffect(() => subscribeResumedChecklistMedia(event => {
        if (event.checklistId === checklistId && event.fieldId === fieldId &&
            ((value ?? '') === event.expectedValue || value === event.localUri)) onChange(event.uri);
    }), [checklistId, fieldId, value, onChange]);
    const safeAreaInsets = useSafeAreaInsets();
    const resolvedUri = useChecklistMediaPreview(value, APP_CONSTANTS.DATABASE.TABLES.CHECKLIST_PHOTOS.NAME, 'image/jpeg', { companyId: empresaId, checklistId, fieldId });
    const notifyPhotoFlowState = React.useCallback((active: boolean, reason: string) => {
        onPhotoFlowStateChange?.({
            fieldId,
            active,
            reason,
            at: Date.now(),
        });
    }, [fieldId, onPhotoFlowStateChange]);

    useEffect(() => {
        if (/^https?:\/\//.test(value ?? '') || isStorageMediaReference(value)) setPreviewUri(null);
    }, [value]);

    // Função para mostrar alerta adaptável à plataforma
    const showMessage = (
        title: string,
        msg: string,
        buttons?: AlertButton[]
    ) => {
        if (Platform.OS !== 'android') {
            showAlert(title, msg, buttons ?? [{ text: 'OK' }]);
        } else {
            Alert.alert(title, msg, buttons ?? [{ text: 'OK' }]);
        }
    };

    // Determina qual imagem mostrar (preview, resolvida, valor atual, ou placeholder)
    const candidateUri = isStorageMediaReference(value) ? resolvedUri : previewUri ?? resolvedUri;
    const retainedPreview = useRetainedLocalMediaPreview(candidateUri, `${empresaId}:${checklistId}:${fieldId}`);
    const uriToShow = retainedPreview.visibleUri;
    useEffect(() => {
        const cleanup = previewCleanup.current;
        cleanup.mounted = true;
        return () => {
            cleanup.mounted = false;
            for (const uri of cleanup.uris) URL.revokeObjectURL(uri);
            cleanup.uris.clear();
        };
    }, []);
    // Libera apenas após o processamento e o commit que retirou o blob da imagem.
    useEffect(() => {
        for (const uri of previewCleanup.current.uris) {
            if (uri !== uriToShow) {
                URL.revokeObjectURL(uri);
                previewCleanup.current.uris.delete(uri);
            }
        }
    });
    const shouldShowImageSource = APP_CONSTANTS.FEATURE_FLAGS?.SHOW_DEBUG_ACTIONS === true &&
        APP_CONSTANTS.DEBUG?.SHOW_IMAGE_SOURCE === true;
    useEffect(() => {
        if (uriToShow) {
            traceImage('component.photo.renderSource', { checklistId, campoId: fieldId, src: uriToShow });
        }
    }, [checklistId, fieldId, uriToShow]);

    // Abre o menu de seleção de imagem
    const handlePickImage = () => {
        if (!editable) return;
        showActionSheetWithOptions(
            {
                options: ['Cancelar', 'Tirar foto', 'Galeria'],
                cancelButtonIndex: 0,
                title: 'Selecionar foto',
                message: 'De onde você quer obter a foto?',
                titleTextStyle: {
                    color: theme.colors.text.primary,
                    fontSize: theme.fontSizes.md,
                    fontWeight: theme.fontWeights.medium as any,
                },
                messageTextStyle: {
                    color: theme.colors.text.secondary,
                    fontSize: theme.fontSizes.sm,
                },
                containerStyle: {
                    backgroundColor: theme.colors.background.primary,
                    ...getAndroidActionSheetSafeAreaStyle({
                        bottomInset: safeAreaInsets.bottom,
                    }),
                },
                textStyle: {
                    color: theme.colors.primary.default,
                    fontSize: theme.fontSizes.md,
                },
            },
            async (buttonIndex) => {
                if (buttonIndex === 1) {
                    await takePhoto();
                }
                if (buttonIndex === 2) {
                    await pickFromLibrary();
                }
            }
        );
    };

    // Tira uma foto usando a câmera
    const takePhoto = async () => {
        if (!editable) return;
        const captureAttemptId = uuidv4();
        await beginMediaAttempt({ id: captureAttemptId, checklistId, fieldId, source: 'camera' });
        const recordCapture = createMediaDiagnosticRecorder({ checklistId, fieldId, attemptId: captureAttemptId });
        recordCapture('capture_started');
        notifyPhotoFlowState(true, 'camera_start');
        try {
            const outcome = await takePhotoFromCamera({
                mediaTypes: ['images'],
                allowsEditing: false,
                aspect: [4, 3],
                quality: 0.7,
            }, {
                source: 'checklist_photo_field',
                screen: 'checklist/[id]',
                action: 'take_checklist_photo_with_camera',
                entityType: 'checklist_photo',
                entityId: fieldId,
                checklistId,
                empresaId,
            });

            if (outcome.status === 'denied') {
                recordCapture('capture_denied');
                finishMediaAttempt(captureAttemptId);
                showMessage('Permissão negada', 'Precisamos de permissão para acessar a câmera.');
                return;
            }

            if (outcome.status === 'failed') {
                recordCapture('capture_failed', outcome.error);
                finishMediaAttempt(captureAttemptId, true, { phase: 'capture_failed', errorCode: mediaErrorCode(outcome.error) });
                ChecklistPhotoFieldLogger.Main.warn(
                    `[takePhoto] Falha ao abrir camera: ${outcome.error.message}`
                );
                showMessage('Erro', 'Não foi possível tirar a foto.');
                return;
            }

            if (outcome.status !== 'success' || !outcome.result.assets?.length) { recordCapture('capture_cancelled'); finishMediaAttempt(captureAttemptId); }
            if (outcome.status === 'success' && outcome.result.assets?.length > 0) {
                const selectedImage = outcome.result.assets[0];
                setPreviewUri(selectedImage.uri);
                await handleSelectedImage(selectedImage, captureAttemptId);
            }
        } catch (error) {
            recordCapture('capture_failed', error);
            finishMediaAttempt(captureAttemptId, true, { phase: 'capture_failed', errorCode: mediaErrorCode(error) });
            showMessage('Erro', 'Não foi possível tirar a foto.');
        } finally {
            notifyPhotoFlowState(false, 'camera_finished');
        }
    };

    // Seleciona uma imagem da galeria
    const pickFromLibrary = async () => {
        if (!editable) return;
        const captureAttemptId = uuidv4();
        await beginMediaAttempt({ id: captureAttemptId, checklistId, fieldId, source: 'gallery' });
        const recordCapture = createMediaDiagnosticRecorder({ checklistId, fieldId, attemptId: captureAttemptId });
        recordCapture('capture_started');
        notifyPhotoFlowState(true, 'gallery_start');
        try {
            const outcome = await pickImageFromLibrary({
                mediaTypes: ImagePicker.MediaTypeOptions.Images,
                allowsEditing: false,
                aspect: [4, 3],
                quality: 0.7,
            }, {
                source: 'checklist_photo_field',
                screen: 'checklist/[id]',
                action: 'pick_checklist_photo_from_library',
                entityType: 'checklist_photo',
                entityId: fieldId,
                checklistId,
                empresaId,
            });

            if (outcome.status === 'denied') {
                recordCapture('capture_denied');
                finishMediaAttempt(captureAttemptId);
                showMessage('Permissão negada', 'Precisamos de permissão para acessar suas fotos.');
                return;
            }

            if (outcome.status === 'failed') {
                recordCapture('capture_failed', outcome.error);
                finishMediaAttempt(captureAttemptId, true, { phase: 'capture_failed', errorCode: mediaErrorCode(outcome.error) });
                ChecklistPhotoFieldLogger.Main.warn(
                    `[pickFromLibrary] Falha ao abrir galeria: ${outcome.error.message}`
                );
                showMessage('Erro', 'Não foi possível selecionar a imagem.');
                return;
            }

            if (outcome.status !== 'success') {
                recordCapture('capture_cancelled');
                finishMediaAttempt(captureAttemptId);
                return;
            }

            const selectedImage = outcome.result.assets[0];
            if (!selectedImage) {
                recordCapture('capture_cancelled');
                finishMediaAttempt(captureAttemptId);
                return;
            }

            setPreviewUri(selectedImage.uri);
            await handleSelectedImage(selectedImage, captureAttemptId);
        } catch (error) {
            recordCapture('capture_failed', error);
            finishMediaAttempt(captureAttemptId, true, { phase: 'capture_failed', errorCode: mediaErrorCode(error) });
            ChecklistPhotoFieldLogger.Main.warn(
                `[pickFromLibrary] Exceção inesperada: ${error}`
            );
            showMessage('Erro', 'Não foi possível selecionar a imagem.');
        } finally {
            notifyPhotoFlowState(false, 'gallery_finished');
        }
    };

    // Faz o upload da imagem e atualiza o draft
    const handleSelectedImage = async (asset: ImagePicker.ImagePickerAsset, captureAttemptId?: string): Promise<boolean> => {
        const mediaAttemptId = captureAttemptId ?? uuidv4();
        if (!captureAttemptId) await beginMediaAttempt({ id: mediaAttemptId, checklistId, fieldId, source: 'camera' });
        const recordMedia = createMediaDiagnosticRecorder({ checklistId, fieldId, attemptId: mediaAttemptId });
        recordMedia('capture_received');
        ChecklistPhotoFieldLogger.Main.info('[handleSelectedImage] Iniciando seleção de foto');
        notifyPhotoFlowState(true, 'image_processing_start');
        setUploading(true);
        let localPersisted = false;
        try {
            let localUri = asset.uri;
            // Fluxo especial para web: salvar base64 na tabela CHECKLIST_PHOTOS
            if (Platform.OS === 'web') {
                // Obtém tamanho original (bytes) via fetch antes da compressão
                let originalSizeKB = 0;
                try {
                    const origBlob = await fetch(asset.uri).then(r => r.blob());
                    originalSizeKB = origBlob.size / 1024;
                } catch {}

                ChecklistPhotoFieldLogger.Main.info(
                    `[handleSelectedImage][WEB] Tamanho original: ${originalSizeKB.toFixed(1)} KB`
                );

                const base64 = await compressChecklistPhotoWeb(asset.uri, {
                    maxWidth: 1280,
                    quality: 0.75,
                });

                // 2) Sempre salvamos como JPEG depois da compressão
                const compressedSizeKB = (base64.length * 0.75) / 1024; // chars->bytes approx 3/4
                const reduction = originalSizeKB ? ((1 - compressedSizeKB / originalSizeKB) * 100).toFixed(0) : 'N/A';

                ChecklistPhotoFieldLogger.Main.info(
                    `[handleSelectedImage][WEB] Tamanho comprimido: ${compressedSizeKB.toFixed(1)} KB (redução ${reduction}%)`
                );

                const extension = 'jpg';
                ChecklistPhotoFieldLogger.Main.info(
                    `[handleSelectedImage] Imagem comprimida para JPEG, tamanho base64=${(base64.length / 1024).toFixed(1)}KB`
                );
                // Gerar id único para a imagem, agora com extensão
                const destPath = `checklist-photos/${empresaId}/${checklistId}-${fieldId}-${Date.now()}-${mediaAttemptId}.${extension}`;
                ChecklistPhotoFieldLogger.Main.info('[handleSelectedImage] Salvando imagem no IndexedDB');
                // Salvar na tabela CHECKLIST_PHOTOS
                await databaseService.createItem(
                    APP_CONSTANTS.DATABASE.TABLES.CHECKLIST_PHOTOS.NAME,
                    {
                        id: destPath,
                        base64,
                        created_at: Date.now(),
                        updated_at: Date.now(),
                    },
                    true // skipPendingOperation
                );
                localUri = destPath;
            } else {
                // Mobile: compactar/redimensionar imagem antes de salvar
                ChecklistPhotoFieldLogger.Main.info('[handleSelectedImage] Mobile: Iniciando compressão com expo-image-manipulator');

                // Tamanho original
                let originalSizeKBMobile = 0;
                try {
                    const info = await FileSystem.getInfoAsync(asset.uri);
                    if (info.exists && info.size) originalSizeKBMobile = info.size / 1024;
                } catch {}

                ChecklistPhotoFieldLogger.Main.info(
                    `[handleSelectedImage] Mobile: Tamanho original ${originalSizeKBMobile.toFixed(1)} KB`
                );

                const manipulated = await (
                    (ImageManipulator as any).manipulate ?? ImageManipulator.manipulateAsync
                )(
                    asset.uri,
                    [{ resize: { width: 1280 } }],
                    {
                        compress: 0.75,
                        format: ImageManipulator.SaveFormat.JPEG,
                        base64: true,
                    }
                );

                const compressedSizeKBMobile = manipulated.base64
                    ? (manipulated.base64.length * 0.75) / 1024
                    : 0;
                const reductionMobile = originalSizeKBMobile
                    ? ((1 - compressedSizeKBMobile / originalSizeKBMobile) * 100).toFixed(0)
                    : 'N/A';

                ChecklistPhotoFieldLogger.Main.info(
                    `[handleSelectedImage] Mobile: Compressão concluída (${manipulated.width}x${manipulated.height}) – tamanho ${compressedSizeKBMobile.toFixed(1)} KB (redução ${reductionMobile}%)`
                );

                const base64Data = manipulated.base64;
                ChecklistPhotoFieldLogger.Main.info(`[handleSelectedImage] base64Data length: ${(base64Data.length/1024).toFixed(1)} KB`);
                // Gerar caminho único para o arquivo
                const timestamp = Date.now();
                const fileName = `${checklistId}-${fieldId}-${timestamp}-${mediaAttemptId}.jpg`;
                const destPath = `checklist-photos/${empresaId}/${fileName}`;
                
                ChecklistPhotoFieldLogger.Main.info('[handleSelectedImage] Mobile: Salvando foto como arquivo');
                
                // Salvar o arquivo no sistema de arquivos usando FileSystem
                const fileUri = `${FileSystem.documentDirectory}${destPath}`;
                
                // Criar diretório se necessário
                const dirPath = `${FileSystem.documentDirectory}checklist-photos/${empresaId}/`;
                const dirInfo = await FileSystem.getInfoAsync(dirPath);
                if (!dirInfo.exists) {
                    await FileSystem.makeDirectoryAsync(dirPath, { intermediates: true });
                }
                
                // Escrever o arquivo
                await FileSystem.writeAsStringAsync(fileUri, base64Data, {
                    encoding: FileSystem.EncodingType.Base64
                });
                
                ChecklistPhotoFieldLogger.Main.info('[handleSelectedImage] Mobile: Arquivo salvo localmente');
                localUri = fileUri;
                

            }
            
            ChecklistPhotoFieldLogger.Main.info('[handleSelectedImage] Chamando uploadChecklistPhoto');
            
            recordMedia('local_file_ready');
            const result = await uploadChecklistPhoto(checklistId, fieldId, localUri, (persistedUri) => {
                localPersisted = true;
                onChange(persistedUri);
                setPreviewUri(null);
                // A recuperação da câmera ainda precisa da origem se a preparação falhar.
                if (Platform.OS !== 'web' && asset.uri !== persistedUri) {
                    void FileSystem.deleteAsync(asset.uri, { idempotent: true }).catch(error => {
                        ChecklistPhotoFieldLogger.Main.warn('Não foi possível remover a origem já preservada:', error);
                    });
                }
            }, mediaAttemptId, value ?? '');
            if (result.superseded) return true;

            if (result.success && result.data?.uri) {
                ChecklistPhotoFieldLogger.Main.info(`[handleSelectedImage] ${result.remoteAcknowledged === true ? 'Mídia enviada; referência remota confirmada.' : 'Mídia salva localmente; envio pendente.'}`);
                // CORREÇÃO: Atualizar o draft com a URL pública IMEDIATAMENTE
                // e aguardar um breve delay para garantir que o onChange seja processado
                onChange(result.data.uri); // Atualiza o draft com a nova URI/URL
                
                // Aguardar um breve momento para garantir que o onChange seja processado
                ChecklistPhotoFieldLogger.Main.info(`[handleSelectedImage] Aguardando 300ms para garantir que o onChange seja processado`);
                await new Promise(resolve => setTimeout(resolve, 300));
                
                setPreviewUri(null); // Limpa o preview para mostrar a URL final
                showChecklistMediaFeedback(result, 'Foto', showMessage, onChange);
            } else {
                ChecklistPhotoFieldLogger.Main.info(`[handleSelectedImage] Upload falhou ou parcial. Erro: ${result.error}`);
                // Limpa preview para usar o value (URI local) em caso de falha
                setPreviewUri(null);
                showChecklistMediaFeedback(result, 'Foto', showMessage, onChange);
                return localPersisted || result.recoveryPending === true;
            }
            return true;
        } catch (err) {
            recordMedia('local_file_failed', err);
            captureSentryExceptionWithScope(err, {
                context: { flow: 'checklist_media', stage: 'local_file', checklistId, campoId: fieldId, attemptId: mediaAttemptId },
                tags: { flow: 'upload', stage: 'local_file' },
            });
            ChecklistPhotoFieldLogger.Main.info(`[handleSelectedImage] Exceção lançada: ${err}`);
            setPreviewUri(null); // Limpa o preview para garantir que a imagem exibida seja a oficial do campo
            showChecklistMediaFeedback({ success: false }, 'Foto', showMessage, onChange);
            return localPersisted;
        } finally {
            if (Platform.OS === 'web' && asset.uri.startsWith('blob:')) {
                if (previewCleanup.current.mounted) previewCleanup.current.uris.add(asset.uri);
                else URL.revokeObjectURL(asset.uri);
                setPreviewUri(null);
            }
            setUploading(false);
            notifyPhotoFlowState(false, 'image_processing_finished');
            // O preview deve ser limpo sempre que o valor do campo foi atualizado via onChange,
            // garantindo que a imagem exibida seja a oficial do campo (local ou remota).
        }
    };

    useEffect(() => {
        if (Platform.OS !== 'android') {
            return;
        }

        let isMounted = true;

        (async () => {
            const recovered = await getRecoveredCameraAsset(checklistId, fieldId, empresaId);
            if (!recovered || !isMounted) {
                return;
            }

            ChecklistPhotoFieldLogger.Main.info(
                `[pendingCamera] processando foto recuperada para checklist=${checklistId} campo=${fieldId}`
            );
            setPreviewUri(recovered.asset.uri);
            const processed = await handleSelectedImage(recovered.asset);
            if (processed) {
                await clearRecoveredCameraAsset(checklistId, fieldId);
                return;
            }

            ChecklistPhotoFieldLogger.Main.warn(
                `[pendingCamera] foto recuperada mantida para retry checklist=${checklistId} campo=${fieldId}`
            );
        })();

        return () => {
            isMounted = false;
        };
    }, [checklistId, empresaId, fieldId]);

    // Função para confirmar e remover a foto
    const handleRemovePhoto = () => {
        if (!editable) return;
        const confirmRemove = () => {
            showMessage(
                'Confirmar remoção',
                'Tem certeza que deseja remover esta foto?',
                [
                    { text: 'Cancelar', style: 'cancel' },
                    { text: 'Remover', style: 'destructive', onPress: performRemove }
                ]
            );
        };

        const performRemove = async () => {
            ChecklistPhotoFieldLogger.Main.info(`[handleRemovePhoto] Iniciando remoção para fieldId=${fieldId}`);
            setDeleting(true);
            try {
                const result = await deleteChecklistPhoto(checklistId, fieldId);
                if (result.success) {
                    ChecklistPhotoFieldLogger.Main.info(`[handleRemovePhoto] Remoção concluída com sucesso`);
                    onChange(''); // Limpa o valor no draft
                    setPreviewUri(null); // Limpa o preview
                    showMessage('Sucesso', 'Foto removida com sucesso!');
                } else {
                    ChecklistPhotoFieldLogger.Main.info(`[handleRemovePhoto] Remoção falhou: ${result.error}`);
                    showMessage('Erro', result.error || 'Falha ao remover a foto.');
                }
            } catch (err) {
                ChecklistPhotoFieldLogger.Main.info(`[handleRemovePhoto] Exceção lançada: ${err}`);
                showMessage('Erro', 'Falha ao remover a foto.');
            } finally {
                setDeleting(false);
                ChecklistPhotoFieldLogger.Main.info(`[handleRemovePhoto] Finalizado para fieldId=${fieldId}`);
            }
        };

        confirmRemove();
    };

    const handleDownloadPhoto = async () => {
        if (!uriToShow) {
            return;
        }

        const sourceKind = uriToShow.startsWith('data:')
            ? 'data'
            : /^https?:\/\//i.test(uriToShow)
                ? 'remote'
                : 'local';

        setDownloading(true);
        ChecklistPhotoFieldLogger.Main.info(
            `[handleDownloadPhoto] Iniciando download da foto (${Platform.OS}, ${sourceKind})`
        );
        addSentryBreadcrumb({
            category: 'checklist.photo.download',
            message: 'Download de foto iniciado',
            data: { platform: Platform.OS, sourceKind },
        });

        try {
            const result = await downloadChecklistPhoto(
                uriToShow,
                `foto-checklist-${Date.now()}`
            );

            if (result.status === 'cancelled') {
                ChecklistPhotoFieldLogger.Main.info('[handleDownloadPhoto] Seleção de pasta cancelada');
                return;
            }

            if (result.status === 'shared') {
                ChecklistPhotoFieldLogger.Main.info('[handleDownloadPhoto] Menu de compartilhamento encerrado no iOS');
                addSentryBreadcrumb({
                    category: 'checklist.photo.download',
                    message: 'Compartilhamento de foto encerrado',
                    data: { platform: Platform.OS, sourceKind },
                });
                return;
            }

            ChecklistPhotoFieldLogger.Main.info('[handleDownloadPhoto] Foto salva com sucesso');
            addSentryBreadcrumb({
                category: 'checklist.photo.download',
                message: 'Download de foto concluído',
                data: { platform: Platform.OS, sourceKind },
            });
            showMessage('Sucesso', 'Foto salva com sucesso!');
        } catch (error) {
            const normalizedError = error instanceof Error ? error : new Error(String(error));
            ChecklistPhotoFieldLogger.Main.warn(
                `[handleDownloadPhoto] Falha ao salvar foto: ${normalizedError.message}`
            );
            captureSentryException(normalizedError, {
                flow: 'checklist_photo_download',
                flowStage: 'save',
                platform: Platform.OS,
                sourceKind,
            });
            showMessage('Erro', 'Não foi possível baixar a foto. Tente novamente.');
        } finally {
            setDownloading(false);
        }
    };

    // Renderização do campo de foto
    return (
        <View testID={`checklist-photo-field-${fieldId}`} style={[styles.container, compactCard && styles.cardContainer]}>
            {!hideLabel && <Text style={styles.label}>{label}</Text>}
            <View style={[styles.imageWrapper, compactCard && styles.cardWrapper]}>
                {uriToShow ? (
                    <TouchableOpacity style={compactCard && styles.cardImageTouch}
                        disabled={retainedPreview.failed && !retainedPreview.pending}
                        onPress={() => setViewingPhoto(true)}>
                        {retainedPreview.pendingUri && <Image
                            key={retainedPreview.visibleGeneration}
                            source={{ uri: uriToShow }} style={[styles.image, compactCard && styles.cardImage, StyleSheet.absoluteFill]}
                            resizeMode="contain"
                            testID={`photo-retained-${fieldId}`}
                        />}
                        <Image
                            key={retainedPreview.generation}
                            source={{ uri: candidateUri! }}
                            resizeMode="contain"
                            style={[styles.image, compactCard && styles.cardImage, retainedPreview.pending && { opacity: 0 }]}
                            onLoad={retainedPreview.onLoad}
                            onError={retainedPreview.onError}
                        />
                    </TouchableOpacity>
                ) : (
                    <View style={[styles.placeholder, compactCard && styles.cardImage]}>
                        <MaterialCommunityIcons name="camera" size={40} color={compactCard ? theme.colors.primary.default : theme.colors.text.inverse} />
                    </View>
                )}
                {retainedPreview.failed && <ChecklistImageLoadNotice uri={candidateUri}
                    onRetry={retainedPreview.retry} testID={`photo-load-status-${fieldId}`} />}
                {shouldShowImageSource && (
                    <Text style={{ fontSize: 11, color: '#666', marginTop: 4, flex: 1 }} numberOfLines={2}>
                        {`src: ${(uriToShow ?? '').slice(0, 120)}`}
                    </Text>
                )}
                
                {/* Botões de ação em coluna quando há imagem */}
                {uriToShow ? (
                    <View style={styles.actionButtons}>
                        <TouchableOpacity
                            style={styles.button}
                            onPress={handleDownloadPhoto}
                            disabled={downloading || uploading || deleting}
                        >
                            {downloading ? (
                                <ActivityIndicator size="small" color={theme.colors.primary.default} />
                            ) : (
                                <Text style={styles.buttonText}>Baixar foto</Text>
                            )}
                        </TouchableOpacity>

                        {editable && <TouchableOpacity
                            style={styles.button}
                            onPress={handlePickImage}
                            disabled={uploading || deleting || downloading}
                        >
                            {uploading ? (
                                <ActivityIndicator size="small" color={theme.colors.primary.default} />
                            ) : (
                                <Text style={styles.buttonText}>Alterar foto</Text>
                            )}
                        </TouchableOpacity>}

                        {editable && <TouchableOpacity
                            style={[styles.button, styles.removeButton]}
                            onPress={handleRemovePhoto}
                            disabled={deleting || uploading || downloading}
                        >
                            {deleting ? (
                                <ActivityIndicator size="small" color={theme.colors.util.error} />
                            ) : (
                                <Text style={[styles.buttonText, { color: theme.colors.util.error }]}>
                                    Remover foto
                                </Text>
                            )}
                        </TouchableOpacity>}
                    </View>
                ) : editable ? (
                    <TouchableOpacity
                        style={styles.button}
                        onPress={handlePickImage}
                        disabled={uploading}
                    >
                        {uploading ? (
                            <ActivityIndicator size="small" color={theme.colors.primary.default} />
                        ) : (
                            <Text style={styles.buttonText}>Selecionar foto</Text>
                        )}
                    </TouchableOpacity>
                ) : <Text style={styles.buttonText}>Nenhuma foto adicionada</Text>}
            </View>

            {/* Modal de visualização da foto */}
            {viewingPhoto && uriToShow && (
                <Modal
                    visible
                    transparent
                    onRequestClose={() => setViewingPhoto(false)}
                    animationType="fade"
                >
                    <TouchableOpacity
                        style={styles.photoViewerOverlay}
                        activeOpacity={1}
                        onPress={() => setViewingPhoto(false)}
                    >
                        <View style={styles.photoViewerContainer}>
                            <Image
                                source={{ uri: uriToShow }}
                                style={styles.photoViewerImage}
                                resizeMode="contain"
                            />
                            <TouchableOpacity
                                style={styles.closeButton}
                                onPress={() => setViewingPhoto(false)}
                            >
                                <MaterialCommunityIcons
                                    name="close"
                                    size={24}
                                    color={theme.colors.text.inverse}
                                />
                            </TouchableOpacity>
                        </View>
                    </TouchableOpacity>
                </Modal>
            )}
        </View>
    );
};

// Estilos minimalistas e elegantes
const styles = StyleSheet.create({
    cardContainer: { marginVertical: 0 },
    cardWrapper: { flexDirection: 'column', alignItems: 'stretch', gap: theme.spacing.sm },
    cardImageTouch: { width: '100%' },
    cardImage: { width: '100%', height: 150, marginRight: 0, borderWidth: 1, borderColor: theme.colors.border.light },
    container: {
        marginVertical: 12,
    },
    label: {
        fontSize: theme.fontSizes.sm,
        color: theme.colors.text.secondary,
        marginBottom: theme.components.checklistEditor.spacing.labelGap,
    },
    imageWrapper: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        rowGap: 8,
        alignItems: 'center',
    },
    image: {
        width: 80,
        height: 80,
        borderRadius: 8,
        marginRight: 12,
        backgroundColor: theme.colors.background.secondary,
    },
    placeholder: {
        width: 80,
        height: 80,
        borderRadius: 8,
        backgroundColor: theme.colors.primary.light,
        justifyContent: 'center',
        alignItems: 'center',
        marginRight: 12,
    },
    button: {
        maxWidth: '100%',
        borderWidth: 1,
        borderColor: theme.colors.primary.default,
        borderRadius: theme.borderRadius.sm,
        paddingHorizontal: theme.spacing.sm,
        paddingVertical: theme.spacing.xs,
    },
    buttonText: {
        color: theme.colors.primary.default,
        fontSize: theme.fontSizes.xs,
        fontWeight: theme.fontWeights.medium as any,
    },
    actionButtons: {
        flexDirection: 'column',
        gap: theme.spacing.xs,
    },
    removeButton: {
        borderColor: theme.colors.util.error,
    },
    photoViewerOverlay: {
        flex: 1,
        backgroundColor: theme.colors.overlay.dark,
        justifyContent: 'center',
        alignItems: 'center',
    },
    photoViewerContainer: {
        width: theme.components.modal.photoViewer.containerSize.width,
        height: theme.components.modal.photoViewer.containerSize.height,
        justifyContent: 'center',
        alignItems: 'center',
        position: 'relative',
    },
    photoViewerImage: {
        width: '100%' as const,
        height: '100%' as const,
    },
    closeButton: {
        position: 'absolute',
        top: theme.components.modal.photoViewer.closeButton.position.top,
        right: theme.components.modal.photoViewer.closeButton.position.right,
        backgroundColor: theme.colors.overlay.semi,
        borderRadius: theme.components.modal.photoViewer.closeButton.size / 2,
        width: theme.components.modal.photoViewer.closeButton.size,
        height: theme.components.modal.photoViewer.closeButton.size,
        justifyContent: 'center',
        alignItems: 'center',
    },
});

export default ChecklistPhotoField; 
